/**
 * Sheet „Vertrag anlegen" (Spec 03, „Sheet Vertrag anlegen"). Vier Felder
 * insgesamt (Entscheidung 4) — das Vertragsobjekt steht als Text im Kopf
 * (`contract-create-subject`, aus dem Kontext, nicht bedienbar), die
 * restlichen drei sind Mieter, Beginn und Miete.
 *
 * **Kein** „Anlegen und noch eins" (bewusste Abweichung vom 02a-Muster, siehe
 * Spec 03: der Knopf wäre vor allem eine Einladung zum versehentlichen
 * Doppelanlegen desselben Vertrags — zwei laufende Verträge am selben Objekt
 * sind zwar zulässig, aber kein Ablauf, den man in einem Zug erfasst).
 *
 * Das Mietersheet (`features/tenants/TenantCreateSheet`) öffnet **über**
 * diesem hier — komponiert von der Route (`routes/ObjectDetail.tsx`), weil
 * `features/contracts` `features/tenants` nicht kennt
 * (`apps/web/CLAUDE.md`, „features kennen einander nicht"). `pendingTenant`
 * ist der Weg zurück: Ändert sich seine `id`, übernimmt dieses Sheet ihn als
 * Auswahl, ohne dass der Aufrufer eine „schon verarbeitet"-Quittung braucht —
 * ein frischer Öffnen-Durchlauf merkt sich den zu diesem Zeitpunkt aktuellen
 * `pendingTenant` als bereits erledigt (siehe `wasOpen`-Block unten), sodass
 * ein Rest aus einer früheren Sitzung nicht erneut zuschlägt.
 */

import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ApiError } from '@/lib/api'
import { parseCents } from '@/lib/money'
import { useCreateContract } from '@/features/contracts/api'
import { FIELD_CLASS, FormField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { TenantPicker } from '@/features/contracts/TenantPicker'
import { DEFAULT_PAYMENT_DUE_DAY, contractCreateBody } from '@/features/contracts/contract-create'
import { parseIntegerInput } from '@/features/contracts/integer'
import type { TenantSelection } from '@/features/contracts/TenantPicker'

interface ContractCreateSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly subjectId: string
  readonly subjectLabel: string
  readonly onRequestNewTenant: () => void
  /** Der zuletzt aus dem Mietersheet zurückgekommene Mieter — siehe Kopf
   * dieser Datei. `null`, solange keiner zurückgekommen ist. */
  readonly pendingTenant: TenantSelection | null
  readonly onCreated: (contractId: string) => void
}

/** Der heutige Monatserste, als `date`-Eingabewert (Spec 03, „Sheet Vertrag
 * anlegen": „vorbelegt mit dem heutigen Monatsersten"). Client-seitige
 * Formularvorbelegung, keine Fachregel — der Nutzer ändert sie frei, und der
 * Server prüft `starts_on` unabhängig davon. */
function defaultStartsOn(): string {
  const now = new Date()
  return `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
}

export function ContractCreateSheet({
  open,
  onOpenChange,
  subjectId,
  subjectLabel,
  onRequestNewTenant,
  pendingTenant,
  onCreated,
}: ContractCreateSheetProps) {
  const createMutation = useCreateContract()
  const tenantSearchRef = useRef<HTMLInputElement>(null)

  const [selectedTenant, setSelectedTenant] = useState<TenantSelection | null>(null)
  const [startsOn, setStartsOn] = useState(defaultStartsOn())
  const [rent, setRent] = useState('')
  const [paymentDueDay, setPaymentDueDay] = useState(DEFAULT_PAYMENT_DUE_DAY)
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)

  // Jedes Öffnen beginnt frisch — dieselbe Technik wie in `BuildingSheet`/
  // `UnitSheet` (React-Muster „Adjusting state when a prop changes").
  // `syncedPendingId` wird dabei auf den *aktuellen* `pendingTenant` gesetzt,
  // nicht auf `undefined`: Ein Rest aus einer früheren Sitzung soll beim
  // Neuöffnen nicht automatisch übernommen werden, nur ein Mieter, der
  // *während* dieser Sitzung aus dem Mietersheet zurückkommt.
  const [wasOpen, setWasOpen] = useState(open)
  const [syncedPendingId, setSyncedPendingId] = useState(pendingTenant?.id)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setSelectedTenant(null)
      setStartsOn(defaultStartsOn())
      setRent('')
      setPaymentDueDay(DEFAULT_PAYMENT_DUE_DAY)
      setSyncedPendingId(pendingTenant?.id)
      setFieldError(undefined)
      setGeneralError(undefined)
    }
  }

  if (pendingTenant !== null && pendingTenant.id !== syncedPendingId) {
    setSyncedPendingId(pendingTenant.id)
    setSelectedTenant(pendingTenant)
  }

  const parsedRent = parseCents(rent)
  const parsedPaymentDueDay = parseIntegerInput(paymentDueDay)
  const canSubmit =
    selectedTenant !== null && startsOn !== '' && parsedRent !== null && parsedPaymentDueDay !== null

  async function submit() {
    if (selectedTenant === null || parsedRent === null || parsedPaymentDueDay === null) return
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      const created = await createMutation.mutateAsync({
        body: contractCreateBody({
          subjectId,
          tenantId: selectedTenant.id,
          startsOn,
          rentCents: parsedRent,
          paymentDueDay: parsedPaymentDueDay,
        }),
      })
      onOpenChange(false)
      onCreated(created.id)
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    await submit()
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          tenantSearchRef.current?.focus()
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
      >
        <form onSubmit={(event) => void handleSubmit(event)} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">Vertrag anlegen</SheetTitle>
            <p data-testid="contract-create-subject" className="text-label text-muted">
              {subjectLabel}
            </p>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <TenantPicker
              testId="contract-create-tenant"
              value={selectedTenant}
              onSelect={setSelectedTenant}
              onRequestNewTenant={onRequestNewTenant}
              error={fieldError}
              errorPrefix="contract-create"
              searchRef={tenantSearchRef}
            />

            <FormField
              label="Beginn"
              testId="contract-create-starts-on"
              apiField="starts_on"
              errorPrefix="contract-create"
              type="date"
              required
              value={startsOn}
              onChange={setStartsOn}
              error={fieldError}
            />

            <FormField
              label="Miete"
              testId="contract-create-rent"
              apiField="base_rent_net_cents"
              errorPrefix="contract-create"
              inputMode="decimal"
              required
              value={rent}
              onChange={setRent}
              error={fieldError}
            />

            <FormField
              label="Zahltag"
              testId="contract-create-payment-due-day"
              apiField="payment_due_day"
              errorPrefix="contract-create"
              inputMode="numeric"
              value={paymentDueDay}
              onChange={setPaymentDueDay}
              error={fieldError}
            />
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="contract-create-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="contract-create-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                data-testid="contract-create-save"
                disabled={!canSubmit || createMutation.isPending}
                aria-busy={createMutation.isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                Vertrag anlegen
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
